import { all, call, put, fork, takeEvery, select } from 'redux-saga/effects';
import {
    fetchTeamsSuccess,
    showTeamMessage,
    onSaveTeamSuccess,
    onDeleteTeamSuccess,
    addFavouriteTeamSuccess,
    onFetchSelectMemberDataSuccess,
} from 'actions/team';
import {
    ON_SAVE_TEAM,
    FETCH_ALL_TEAM,
    ON_DELETE_TEAM,
    ADD_FAVOURITE_TEAM,
    ON_FETCH_SELECT_MEMBER_DATA,
    ON_TEAM_MEMBER_UPDATE,
} from 'constants/ActionTypes';
import { fetchDataRequest, addOrUpdateDataRequest, updateDataRequest, deleteDataRequest  } from 'services/ApiService';
import { apiErrorMessageFormatter } from 'services/errorService';
import { ORGID_PATH_PARAM } from 'constants/api';
import { mapDataToSelectionData } from 'services/selectors';

const currentOrg = (state) => state.orgs.selectedOrg;
const getTeamList = (state) => state.teams.allTeam;
const getSelectedTeam = (state) => state.teams.selectedTeam;
const getMemberList = (state) => state.members.allMember;
const getSelectedMembers = (state) => state.teams.memberSelectionList;

export function* fetchTeams() {
    try {
        const selectedOrg = yield select(currentOrg);
        const fetchedTeams = yield call(
            fetchDataRequest,
            'teams',
            [ORGID_PATH_PARAM],
            [selectedOrg ?.id]
        ) || [];
        yield put(fetchTeamsSuccess(fetchedTeams));
    } catch (error) {
        yield put(showTeamMessage(apiErrorMessageFormatter(error)));
    }
}

export function* deleteTeam({ payload }) {
    try {
        // payload should be the teamId
        const selectedOrg = yield select(currentOrg);
        yield call(
            deleteDataRequest,
            'deleteTeam',
            'Deleting team '.concat(payload),
            [ORGID_PATH_PARAM, 'teamId'],
            [selectedOrg ?.id, payload]
        ) || [];
        yield put(onDeleteTeamSuccess(payload)); // just dispatch the id as the reducer will remove from the item from the list
    } catch (error) {
        yield put(showTeamMessage(apiErrorMessageFormatter(error)));
    }
}

export function* saveTeam({ payload }) {
    try {
        const selectedOrg = yield select(currentOrg);
        const allTeams = yield select(getTeamList);
        let finalPayload = { ...payload };
        // check the orgid of the payload - it will be empty for a new payload - populate from the selectedOrg - otherwise leave it as is
        if (payload ?.orgid === '') {
            finalPayload = { ...payload, orgid: selectedOrg ?.id };
        }
        const savedTeam = yield call(
            addOrUpdateDataRequest,
            allTeams,
            finalPayload,
            'addTeam',
            'updateTeam',
            [ORGID_PATH_PARAM],
            [selectedOrg.id]);
        yield put(onSaveTeamSuccess(savedTeam));
    } catch (error) {
        yield put(showTeamMessage(apiErrorMessageFormatter(error)));
    }
}

/**
 * This is when the user selects the star on the list page - update the starred attribute of the entity
 * @param {*} param0 
 */
export function* addFavouriteTeam({ payload }) {
    try {
        const allTeams = yield select(getTeamList);
        const selectedOrg = yield select(currentOrg);
        const filtered = allTeams.filter((entry) => entry ?.id === payload ?.id);

        if (filtered ?.length > 0) {
            const entry = filtered[0];
            const updatedTeam = {
                ...entry,
                starred: !payload.starred
            };
            const auditMsg = 'Updating starred setting to '.concat(updatedTeam ?.starred).concat(' for team ').concat(updatedTeam ?.id);
            yield call(
                updateDataRequest,
                updatedTeam,
                'updateTeam',
                auditMsg,
                [ORGID_PATH_PARAM],
                [selectedOrg.id],
            );
            // now just pass on the original payload as the reducer will filter the list as per the state of the selection
            yield put(addFavouriteTeamSuccess(payload));
        }
    } catch (error) {
        yield put(showTeamMessage(error.messages));
    }
}

export function* onFetchSelectMemberData() {
    const selectedTeam = yield select(getSelectedTeam);
    const allMembers = yield select(getMemberList);
    const selectedDrillDownItems = (Array.isArray(selectedTeam?.members)) ? selectedTeam?.members : [];
    const selections = mapDataToSelectionData(allMembers, selectedDrillDownItems) || null;
    yield put(onFetchSelectMemberDataSuccess(selections));
}

export function* onTeamMemberUpdate() {
    try {
        // go through each  - check if entry is being added or removed and cleanup the other object being referenced
        const selectedOrg = yield select(currentOrg);
        const selectedTeam = yield select(getSelectedTeam);
        const selectedMembers = yield select(getSelectedMembers);
        const updatedDrillDownSelections = new Array();
        selectedMembers.forEach((selection) => {
            if (selection?.selected) {
                updatedDrillDownSelections.push(selection?.id);
            }
        });
        const updatedSelectedTeam = {
            ...selectedTeam,
            members: updatedDrillDownSelections
        };
        const auditMsg = 'Updating selected member to '.concat(' for team ').concat(updatedSelectedTeam ?.id);
        yield call(
            updateDataRequest,
            updatedSelectedTeam,
            'updateTeam',
            auditMsg,
            [ORGID_PATH_PARAM],
            [selectedOrg.id],
        );
        yield put(onSaveTeamSuccess(updatedSelectedTeam));
    } catch (error) {
        yield put(showTeamMessage(apiErrorMessageFormatter(error)));
    }
}

export function* fetchTeamsSaga() {
    yield takeEvery(FETCH_ALL_TEAM, fetchTeams);
}

export function* saveTeamSaga() {
    yield takeEvery(ON_SAVE_TEAM, saveTeam);
}

export function* deleteTeamSaga() {
    yield takeEvery(ON_DELETE_TEAM, deleteTeam);
}

export function* addFavouriteTeamSaga() {
    yield takeEvery(ADD_FAVOURITE_TEAM, addFavouriteTeam);
}

export function* onFetchSelectMemberDataSaga() {
    yield takeEvery(ON_FETCH_SELECT_MEMBER_DATA, onFetchSelectMemberData);
}

export function* onTeamMemberUpdateSaga() {
    yield takeEvery(ON_TEAM_MEMBER_UPDATE, onTeamMemberUpdate);
}

export default function* rootSaga() {
    yield all([
        fork(fetchTeamsSaga),
        fork(saveTeamSaga),
        fork(deleteTeamSaga),
        fork(addFavouriteTeamSaga),
        fork(onFetchSelectMemberDataSaga),
        fork(onTeamMemberUpdateSaga),
    ]);
}


// WEBPACK FOOTER //
// ./sagas/team.js